/**
 * Data layer for `fully-customizable-dashboard-widgets`. Thin typed fetch wrappers over /api/v1/fully-customizable-dashboard-widgets.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface FullyCustomizableDashboardWidgetsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListFullyCustomizableDashboardWidgetsResponse {
	count: number;
	items: FullyCustomizableDashboardWidgetsRow[];
}

export interface CreateFullyCustomizableDashboardWidgetsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/fully-customizable-dashboard-widgets";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListFullyCustomizableDashboardWidgetsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListFullyCustomizableDashboardWidgetsResponse>(res);
}

export async function create(body: CreateFullyCustomizableDashboardWidgetsBody): Promise<FullyCustomizableDashboardWidgetsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<FullyCustomizableDashboardWidgetsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const fullyCustomizableDashboardWidgetsQueryKeys = {
	all: ["fully-customizable-dashboard-widgets"] as const,
	list: () => [...fullyCustomizableDashboardWidgetsQueryKeys.all] as const,
	detail: (id: string) => [...fullyCustomizableDashboardWidgetsQueryKeys.all, id] as const,
};
